/**
 * Gemstone bundle sync (configurator / synced gemstone lines only):
 * - Remove gemstone → also remove linked cert/setting lines.
 * - Remove cert or setting only → gemstone stays in cart.
 * - Refreshes cart subtotal/footer after bundle cleanup (Dawn cart page + drawer).
 */
(function () {
  'use strict';

  const BUNDLE_PROP = '_gj_bundle_id';
  const COMPONENT_PROP = '_gj_component';
  const CONFIGURED_PROP = '_gj_configured';
  const RECONCILE_DELAY_MS = 120;
  const CART_MUTATION_RE = /\/cart\/(change|update)(\.js)?(\?|$)/;
  let reconciling = false;
  let reconcileTimer = null;

  async function fetchCart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    return res.ok ? res.json() : null;
  }

  function itemProps(item) {
    const raw = (item && item.properties) || {};
    if (Array.isArray(raw)) {
      const out = {};
      raw.forEach((entry) => {
        if (entry && entry.name) out[entry.name] = entry.value;
      });
      return out;
    }
    return raw;
  }

  function bundleIdFromItem(item) {
    return itemProps(item)[BUNDLE_PROP] || null;
  }

  function formFromProps(p) {
    return p._gj_form || p.Form || '';
  }

  /** True only for lines created by the gemstone configurator. */
  function isGjConfiguratorLine(item) {
    const p = itemProps(item);
    if (!p[BUNDLE_PROP]) return false;
    if (p[CONFIGURED_PROP] === '1') return true;
    if (p[COMPONENT_PROP]) return true;
    return Boolean(formFromProps(p));
  }

  function isPrimaryLine(item) {
    const p = itemProps(item);
    return isGjConfiguratorLine(item) && Boolean(formFromProps(p)) && !p[COMPONENT_PROP];
  }

  function isComponentLine(item) {
    const p = itemProps(item);
    return isGjConfiguratorLine(item) && Boolean(p[COMPONENT_PROP]);
  }

  function cartHasGjConfiguratorLines(cart) {
    return (cart.items || []).some(isGjConfiguratorLine);
  }

  function groupLinesByBundle(cart) {
    const groups = new Map();
    for (const item of cart.items || []) {
      if (!isGjConfiguratorLine(item)) continue;
      const id = bundleIdFromItem(item);
      if (!id) continue;
      if (!groups.has(id)) groups.set(id, []);
      groups.get(id).push(item);
    }
    return groups;
  }

  function getSectionInnerHTML(html, selector) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const el = doc.querySelector(selector);
    return el ? el.innerHTML : '';
  }

  function applyCartPageSections(cartData) {
    const cartItemsEl = document.querySelector('cart-items');
    if (!cartItemsEl || !cartData.sections || typeof cartItemsEl.getSectionsToRender !== 'function') {
      return false;
    }

    const sections = cartItemsEl.getSectionsToRender();
    sections.forEach((section) => {
      const html = cartData.sections[section.section];
      if (!html) return;
      const root = document.getElementById(section.id);
      if (!root) return;
      const target = root.querySelector(section.selector) || root;
      target.innerHTML = getSectionInnerHTML(html, section.selector);
    });

    const itemCount = cartData.item_count;
    if (typeof itemCount === 'number') {
      cartItemsEl.classList.toggle('is-empty', itemCount === 0);
      const cartFooter = document.getElementById('main-cart-footer');
      if (cartFooter) cartFooter.classList.toggle('is-empty', itemCount === 0);
      const drawer = document.querySelector('cart-drawer');
      if (drawer) drawer.classList.toggle('is-empty', itemCount === 0);
    }
    return true;
  }

  async function refreshDrawerSections() {
    const drawerItems = document.querySelector('cart-drawer-items');
    if (drawerItems && typeof drawerItems.onCartUpdate === 'function') {
      await drawerItems.onCartUpdate();
      return true;
    }
    return false;
  }

  async function refreshCartUi(cartData) {
    if (cartData && applyCartPageSections(cartData)) {
      return;
    }
    await refreshDrawerSections();
  }

  async function removeLineKeys(keys) {
    if (!keys.length) return null;
    const updates = {};
    keys.forEach((key) => {
      updates[key] = 0;
    });

    const payload = { updates };
    const cartItemsEl = document.querySelector('cart-items');
    if (cartItemsEl && typeof cartItemsEl.getSectionsToRender === 'function') {
      payload.sections = cartItemsEl.getSectionsToRender().map((section) => section.section);
      payload.sections_url = window.location.pathname;
    }

    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) return null;
    const data = await res.json();
    await refreshCartUi(data);
    return data;
  }

  function notifyCartRefresh(cart) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gj-bundle', cartData: cart });
    }
    document.dispatchEvent(new CustomEvent('gj:bundle:reconciled', { detail: { cart } }));
  }

  async function reconcileBundles() {
    if (reconciling) return false;
    reconciling = true;
    try {
      const cart = await fetchCart();
      if (!cart || !cartHasGjConfiguratorLines(cart)) return false;

      const keysToRemove = new Set();

      for (const lines of groupLinesByBundle(cart).values()) {
        const primary = lines.find(isPrimaryLine);
        const components = lines.filter(isComponentLine);

        // Gemstone removed → drop orphaned cert/setting lines only (gj bundle).
        if (!primary && components.length) {
          components.forEach((line) => keysToRemove.add(line.key));
        }
      }

      if (!keysToRemove.size) return false;

      const updated = await removeLineKeys([...keysToRemove]);
      if (updated) {
        notifyCartRefresh(updated);
      }
      return Boolean(updated);
    } finally {
      reconciling = false;
    }
  }

  function scheduleReconcile() {
    if (reconcileTimer) window.clearTimeout(reconcileTimer);
    reconcileTimer = window.setTimeout(() => {
      reconcileTimer = null;
      reconcileBundles();
    }, RECONCILE_DELAY_MS);
  }

  function isCartMutationUrl(url) {
    if (!url) return false;
    try {
      const path = url.startsWith('http') ? new URL(url, window.location.origin).pathname : url;
      return CART_MUTATION_RE.test(path);
    } catch (e) {
      return CART_MUTATION_RE.test(String(url));
    }
  }

  function installFetchHook() {
    const nativeFetch = window.fetch;
    if (nativeFetch.__gjBundleHook) return;

    window.fetch = function gjBundleFetch(...args) {
      const url = typeof args[0] === 'string' ? args[0] : args[0] && args[0].url;
      const result = nativeFetch.apply(this, args);
      if (isCartMutationUrl(url)) {
        result
          .then((res) => {
            if (res.ok) scheduleReconcile();
            return res;
          })
          .catch(() => null);
      }
      return result;
    };
    window.fetch.__gjBundleHook = true;
  }

  function installXhrHook() {
    if (XMLHttpRequest.prototype.__gjBundleHook) return;
    const nativeOpen = XMLHttpRequest.prototype.open;
    const nativeSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function gjBundleXhrOpen(method, url, ...rest) {
      this.__gjBundleUrl = url;
      return nativeOpen.call(this, method, url, ...rest);
    };

    XMLHttpRequest.prototype.send = function gjBundleXhrSend(...args) {
      if (isCartMutationUrl(this.__gjBundleUrl)) {
        this.addEventListener('load', () => {
          if (this.status >= 200 && this.status < 300) scheduleReconcile();
        });
      }
      return nativeSend.apply(this, args);
    };

    XMLHttpRequest.prototype.__gjBundleHook = true;
  }

  function subscribeToCartUpdates() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') {
      return false;
    }
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event && event.source === 'gj-bundle') return;
      scheduleReconcile();
    });
    return true;
  }

  function boot() {
    installFetchHook();
    installXhrHook();
    scheduleReconcile();

    document.addEventListener('gj:configurator:added', scheduleReconcile);

    if (!subscribeToCartUpdates()) {
      window.addEventListener('load', subscribeToCartUpdates);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
